import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type NoteColor = 'bg-yellow-300' | 'bg-green-300' | 'bg-blue-300' | 'bg-red-300' | 'bg-gray-300';

interface NotesState {
  // Store note colors by note ID
  noteColors: Record<number, NoteColor>;
  
  // Actions
  setNoteColor: (noteId: number, color: NoteColor) => void;
  getNoteColor: (noteId: number) => NoteColor;
  clearNoteColor: (noteId: number) => void;
  clearAllColors: () => void;
}

/**
 * Zustand store for note colors persistence
 * Automatically persists to localStorage using Zustand's persist middleware
 * 
 * Usage:
 *   const { setNoteColor, getNoteColor } = useNotesStore();
 *   setNoteColor(123, 'bg-yellow-300');
 *   const color = getNoteColor(123);
 */
export const useNotesStore = create<NotesState>()(
  persist(
    (set, get) => ({
      // Initial state
      noteColors: {},

      // Actions
      setNoteColor: (noteId, color) =>
        set((state) => ({
          noteColors: {
            ...state.noteColors,
            [noteId]: color,
          },
        })),

      getNoteColor: (noteId) => {
        const state = get();
        // Default to yellow if no color is set
        return state.noteColors[noteId] || 'bg-yellow-300';
      },

      clearNoteColor: (noteId) =>
        set((state) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [noteId]: _removed, ...rest } = state.noteColors;
          return { noteColors: rest };
        }),

      clearAllColors: () =>
        set({ noteColors: {} }),
    }),
    {
      name: 'notes-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
    }
  )
);

